import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { forkJoin, map, Observable, Subject, switchMap } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { Facility, PaginatedList } from 'src/app/shared/models';
import {
  DELETE_SUBMISSION,
  GET_ALL_SUBMISSIONS,
  GET_HOMECENTER,
  GET_NEEM_V3_CONVERSION_DATA,
  GET_SUBMISSION,
  UPDATE_SUBMISSION,
  CONVERT_SBRA_SUBMISSION_TO_V2,
  CONVERT_SBRA_SUBMISSION_TO_V3,
  CONVERT_NEEM_SUBMISSION_TO_V2,
  CONVERT_NEEM_SUBMISSION_TO_V3,
} from 'src/app/shared/graph-queries';
import {
  Submission,
  SubmissionValue,
  SubmissionView,
  SubmissionVersion,
} from '../models';
import { FormGroup } from '@angular/forms';
import { HomeCenter } from 'src/app/shared/models/home-center.model';
import { SubmissionConstants } from '../models/constants/submissions-constants';
import { UsStateService } from 'src/app/shared/forms/services/form-us-state.service';
import { TokenClaimsService } from 'src/app/shared/services';
import { localStorageConstants } from 'src/app/shared/services';
import { FacilityAssignment } from '../../users/models/facility-assignment';
import { NavService } from 'src/app/shared/navigation';
import { NavItemConstants } from 'src/app/shared/navigation/models/constants/nav-item-constants';
import { MutationResult, UpdateResult } from 'src/app/shared/graph-queries/types';

type SubmissionPropertyBindingKey =
  | 'facilityName'
  | 'facilityNumber'
  | 'homeCenterName'
  | 'homeCenterNumber'
  | 'climateZone'
  | 'homeState'
  | 'version'
  | 'designPackage'
  | 'isHeatPumpDocumentOnFile';

function getValueFromSubmissionData(
  data: Submission,
  key: SubmissionPropertyBindingKey,
): string {
  return (data.SubmissionValues ?? []).find((x) => x.PropertyBindingKey === key)
    ?.Value;
}

// Used to extract the correct submission data and map to a submissionValue for the form
interface DataMapping {
  key: string;
  getValue: (
    submissionData: Submission,
    homeinfo: HomeCenter | null,
  ) => string | number | Date;
}

const dataToExtract: DataMapping[] = [
  {
    key: 'facility',
    getValue: (submissionData: Submission, _) =>
      `${getValueFromSubmissionData(
        submissionData,
        'facilityName',
      )} (${getValueFromSubmissionData(submissionData, 'facilityNumber')})`,
  },
  {
    key: 'homeCenter',
    getValue: (submissionData: Submission, _) =>
      `${getValueFromSubmissionData(
        submissionData,
        'homeCenterName',
      )} (${getValueFromSubmissionData(submissionData, 'homeCenterNumber')})`,
  },
  {
    key: 'homeCenterType',
    getValue: (_, homeinfo: HomeCenter) =>
      homeinfo ? homeinfo.HomeCenterType : null,
  },
];

@Injectable({
  providedIn: 'root',
})
export class SubmissionsService {
  submissions$ = new Subject<PaginatedList<SubmissionView>>();
  submission$ = new Subject<Submission>();
  facilities$ = new Subject<Facility[]>();
  currentSubmission: Submission | null = null;

  constructor(
    private apolloClient: Apollo,
    private authService: MsalService,
    private stateService: UsStateService,
    private submissionConstants: SubmissionConstants,
    private tokenService: TokenClaimsService,
    private navConstants: NavItemConstants,
    private navService: NavService,
  ) {
    //fix issue with setting inital nav item to active
    this.navService.setActive(this.navConstants.energyCreditConstant);
  }

  getSubmissions(filter?: any): void {
    let userFacAssignments: FacilityAssignment[] = JSON.parse(
      localStorage.getItem(localStorageConstants.USER_FACILITIES_KEY),
    );

    this.apolloClient
      .query({
        query: GET_ALL_SUBMISSIONS,
        // Always hit the network so the list reflects status changes made on
        // save (e.g. a submission flipped to "Error"/"Submitted") rather than
        // serving the stale cache-first result.
        fetchPolicy: 'no-cache',
        variables: {
          filter: JSON.stringify(filter),
          id: this.authService.instance.getAllAccounts()[0].idTokenClaims.oid,
          userFacilities: userFacAssignments.map(
            (i) => i.Facility.KeyChain.LegacyId,
          ),
        },
      })
      .subscribe((response: any) => {
        let result = response.data.GetAllEnergyCreditSubmissions;
        let submissions = new PaginatedList(result);

        submissions.Items = submissions.Items.map(
          x => new SubmissionView(x),
        );

        //Available Facilities for filter dropdown.
        this.submissions$.next(submissions);
        this.facilities$.next(
          userFacAssignments.map((i) => new Facility(i.Facility)),
        );
      });
  }

  /*
      Function is used to add anything that needs to be added to the submissionValues. This is needed
      in order to properly populate the form (mainly the home info section)
    */
  processSubmissionData(submissionData: Submission, homeinfo: HomeCenter) {
    const existingValues = new Map(
      submissionData.SubmissionValues.map((value) => [
        value.PropertyBindingKey,
        value.Value,
      ]),
    );

    const newValues = dataToExtract
      .filter((mapping) => !existingValues.has(mapping.key))
      .map((mapping) => ({
        PropertyBindingKey: mapping.key,
        Value: mapping.getValue(submissionData, homeinfo),
      }));

    return [...submissionData.SubmissionValues, ...newValues];
  }

  getSubmission(subId: number): void {
    let userFacAssignments: FacilityAssignment[] = JSON.parse(
      localStorage.getItem(localStorageConstants.USER_FACILITIES_KEY),
    );

    this.apolloClient
      .query({
        query: GET_SUBMISSION,
        // Always hit the network. Otherwise, after a save that changes the
        // status server-side (e.g. a failed SBRA submission flips the status
        // to "Error"), the cache-first default would return the stale
        // pre-save submission and the UI would never reflect the new status.
        fetchPolicy: 'no-cache',
        variables: {
          id: subId,
          userFacilities: userFacAssignments.map(
            (i) => i.Facility.KeyChain.LegacyId,
          ),
        },
      })
      .pipe(
        switchMap((submission: any) => {
          let homeNumber = getValueFromSubmissionData(
            submission.data.GetEnergyCreditSubmission,
            'homeCenterNumber',
          );

          return forkJoin({
            homeInfo: this.apolloClient.query({
              query: GET_HOMECENTER,
              variables: { id: homeNumber.toString() },
            }),
            states: this.stateService.getAllStates(),
          }).pipe(
            map((results: any) => ({
              submission: submission.data.GetEnergyCreditSubmission,
              homeinfo: results.homeInfo.data.GetHomeCenterByLegacyId,
              states: results.states,
            })),
          );
        }),
      )
      .subscribe((results: any) => {
        const homeinfo = results.homeinfo;
        const submissionData = results.submission;
        const states = results.states;

        // Get the climate zone and state for the submission
        let climateZone = getValueFromSubmissionData(
          submissionData,
          'climateZone',
        );
        if (!climateZone && submissionData.FormStructureId === 2) {
          let homeState = getValueFromSubmissionData(
            submissionData,
            'homeState',
          );

          if (homeState) {
            climateZone = states
              .find((val: { StateCode: string }) => val.StateCode === homeState)
              ?.HUDClimateZone?.toString();
          }
        }

        localStorage.setItem(
          this.submissionConstants.usStates,
          JSON.stringify(states),
        );
        localStorage.setItem(
          this.submissionConstants.climateZoneKey,
          climateZone,
        );
        localStorage.setItem(
          this.submissionConstants.formStructureIdKey,
          submissionData.FormStructureId,
        );

        const modifiedSubmissionValues = this.processSubmissionData(
          submissionData,
          homeinfo,
        );

        const modifiedSubmission = {
          ...submissionData,
          SubmissionValues: [...modifiedSubmissionValues],
        };

        this.currentSubmission = modifiedSubmission;
        this.submission$.next(modifiedSubmission);
      });
  }

  getNeemV3ConversionData(serialNumber: string): Observable<{
    IsEBuilt: boolean;
    PrimarySpaceHeatingCooling: string | null;
    EnergyEfficiencyType: string | null;
  } | null> {
    return this.apolloClient
      .query<any>({
        query: GET_NEEM_V3_CONVERSION_DATA,
        variables: { payload: serialNumber },
      })
      .pipe(map((r: any) => r.data?.GetNeemV3ConversionData ?? null));
  }

  saveSubmission(
    submission: Submission,
    formData: FormGroup,
  ): Observable<MutationResult> {
    this.updateSubmissionObject(submission, formData);

    return this.apolloClient.mutate<UpdateResult>({
      mutation: UPDATE_SUBMISSION,
      variables: {
        id: submission.Id,
        payload: JSON.stringify({
          Id: submission.Id,
          FormStructureId: submission.FormStructureId,
          SubmissionValues: submission.SubmissionValues,
          ModifiedBy:
            this.tokenService.tokenClaims$.preferred_username.toLowerCase(),
          ModifiedOn: new Date().toUTCString(),
        }),
      },
    });
  }

  private updateSubmissionObject(
    submission: Submission,
    formData: FormGroup,
  ): void {
    // Gets the modified submission values from the form
    Object.keys(formData.value).forEach((key) => {
      this.setSubmissionValue(submission, key, formData.value[key]);
    });

    // Update disabled fields that could have been updated
    ['climateZone'].forEach((key) => {
      this.setSubmissionValue(submission, key, formData.getRawValue()[key]);
    });

    // IF version 2 and not NEEM, update heat pump values
    let version = getValueFromSubmissionData(submission, 'version');
    let facilityNumber = getValueFromSubmissionData(
      submission,
      'facilityNumber',
    );

    if (
      version === SubmissionVersion.VersionTwo.toString() &&
      !Submission.IsNEEMPlant(facilityNumber)
    ) {
      submission = this.updateSbraHeatPumpValues(submission);
    }
  }

  /**
   * Update the SBRA specific heat pump fields for v2 submissions
   * Values are dependent on the design package and whether the heat pump document is on file
   */
  private updateSbraHeatPumpValues(submission: Submission): Submission {
    let isHeatPumpDocumentOnFile = (
      getValueFromSubmissionData(submission, 'isHeatPumpDocumentOnFile') ?? ''
    ).toString();
    let designPackage =
      getValueFromSubmissionData(submission, 'designPackage') ?? '';

    // if sbra design package is set to 1/2/3 EHP
    if (
      this.submissionConstants.designPackageEhpOptions.includes(
        designPackage,
      ) &&
      isHeatPumpDocumentOnFile === 'true'
    ) {
      this.setSubmissionValue(submission, 'indoorSbraBrandId', '1');
      this.setSubmissionValue(submission, 'indoorSbraModelId', '-3');
      this.setSubmissionValue(
        submission,
        'indoorSbraModelWriteIn',
        'Documentation on file',
      );
      this.setSubmissionValue(submission, 'outdoorSbraBrandId', '1');
      this.setSubmissionValue(submission, 'outdoorSbraModelId', '-2');
      this.setSubmissionValue(
        submission,
        'outdoorSbraModelWriteIn',
        'Documentation on file',
      );
    } else {
      this.setSubmissionValue(submission, 'indoorSbraBrandId', null);
      this.setSubmissionValue(submission, 'indoorSbraModelId', null);
      this.setSubmissionValue(submission, 'indoorSbraModelWriteIn', null);
      this.setSubmissionValue(submission, 'outdoorSbraBrandId', null);
      this.setSubmissionValue(submission, 'outdoorSbraModelId', null);
      this.setSubmissionValue(submission, 'outdoorSbraModelWriteIn', null);
      this.setSubmissionValue(submission, 'isHeatPumpDocumentOnFile', false);
    }

    return submission;
  }

  /**
   * Update an existing or add a new submission value to the submission.
   */
  private setSubmissionValue(
    submission: Submission,
    key: string,
    value: any,
  ): void {
    const existingIndex = submission.SubmissionValues.findIndex(
      (val) => val.PropertyBindingKey === key,
    );

    // Submission value already exists, update it's value
    // Otherwise, create a new submission value and add
    if (existingIndex !== -1) {
      submission.SubmissionValues = submission.SubmissionValues.map(
        (val, index) =>
          index === existingIndex ? { ...val, Value: value } : val,
      );
    } else {
      submission.SubmissionValues = [
        ...submission.SubmissionValues,
        {
          PropertyBindingKey: key,
          Value: value,
          SubmissionId: submission.Id,
        } as SubmissionValue,
      ];
    }
  }

  deleteSubmission(id: number): Observable<MutationResult> {
    return this.apolloClient.mutate({
      mutation: DELETE_SUBMISSION,
      variables: {
        id: id,
      },
    });
  }

  convertSbraSubmissionToV2(
    id: number,
  ): Observable<MutationResult<UpdateResult>> {
    return this.runConversionMutation(CONVERT_SBRA_SUBMISSION_TO_V2, id);
  }

  convertNeemSubmissionToV2(
    id: number,
  ): Observable<MutationResult<UpdateResult>> {
    return this.runConversionMutation(CONVERT_NEEM_SUBMISSION_TO_V2, id);
  }

  convertNeemSubmissionToV3(
    id: number,
  ): Observable<MutationResult<UpdateResult>> {
    return this.runConversionMutation(CONVERT_NEEM_SUBMISSION_TO_V3, id);
  }

  convertSbraSubmissionToV3(
    id: number,
  ): Observable<MutationResult<UpdateResult>> {
    return this.runConversionMutation(CONVERT_SBRA_SUBMISSION_TO_V3, id);
  }

  private runConversionMutation(
    mutation: any,
    id: number,
  ): Observable<MutationResult<UpdateResult>> {
    const userEmail =
      this.tokenService.tokenClaims$.preferred_username.toLowerCase();
    const userFacAssignments: FacilityAssignment[] = JSON.parse(
      localStorage.getItem(localStorageConstants.USER_FACILITIES_KEY),
    );
    const userFacilities = userFacAssignments.map(
      (i) => i.Facility.KeyChain.LegacyId,
    );
    return this.apolloClient.mutate({
      mutation,
      variables: {
        id,
        userEmail,
        userFacilities,
      },
    });
  }

}
